import type { SelectItemType } from "@admin-ui/components/base/select/select";
import { router } from "@inertiajs/react";
import type { DateValue } from "@internationalized/date";
import { adminAccountMembersPath, adminAccountSearchPath } from "@travily/routes";
import type { AdminAccountsMembersPersonSearchResult } from "@travily/types/models";
import type AdminAccountsMembersNew from "@travily/types/pages/AdminAccountsMembersNew";
import type { Key, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdminRoutedModal } from "@/components/AdminRoutedModal";
import { type Mode, NewView } from "./new.view";

type SearchState = "idle" | "loading" | "results" | "empty";

const STATUS_ID = "__status__";

const AdminAccountMemberNewPage = ({ account_id }: AdminAccountsMembersNew) => {
  const [mode, setMode] = useState<Mode>("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<AdminAccountsMembersPersonSearchResult[]>([]);
  const [searchState, setSearchState] = useState<SearchState>("idle");
  const [selectedPerson, setSelectedPerson] = useState<AdminAccountsMembersPersonSearchResult | null>(null);
  const [newName, setNewName] = useState("");
  const [newDateOfBirth, setNewDateOfBirth] = useState<DateValue | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortRef = useRef<AbortController>(undefined);
  const justSelectedRef = useRef(false);

  const fetchResults = useCallback(
    async (q: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setSearchState("loading");
      const url = `${adminAccountSearchPath(account_id)}?q=${encodeURIComponent(q)}`;

      try {
        const response = await fetch(url, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (response.ok) {
          const data: AdminAccountsMembersPersonSearchResult[] = await response.json();
          setSearchResults(data);
          setSearchState(data.length > 0 ? "results" : "empty");
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setSearchState("empty");
      }
    },
    [account_id],
  );

  useEffect(() => {
    if (mode !== "search") return;
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setSearchState("idle");
      return;
    }

    setSearchState("loading");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(searchQuery), 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [searchQuery, fetchResults, mode]);

  const handleSelectionChange = (key: Key | null) => {
    if (key == null || key === STATUS_ID) return;
    const person = searchResults.find((p) => p.id === Number(key));
    if (person) {
      justSelectedRef.current = true;
      setSelectedPerson(person);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
    } else {
      setSelectedPerson(null);
    }
  };

  const handleClearSelectedPerson = () => {
    setSelectedPerson(null);
    setSearchQuery("");
  };

  const canSubmit = mode === "search" ? selectedPerson != null : newName.trim().length > 0;

  const items: SelectItemType[] = useMemo(() => {
    if (searchQuery.length < 2) {
      return [{ id: STATUS_ID, label: "Type at least 2 characters to search...", isDisabled: true }];
    }
    if (searchState === "idle" || searchState === "loading") {
      return [{ id: STATUS_ID, label: "Searching...", isDisabled: true }];
    }
    if (searchState === "empty") {
      return [{ id: STATUS_ID, label: `No people found matching "${searchQuery}"`, isDisabled: true }];
    }
    return searchResults.map((person) => ({
      id: person.id,
      label: person.name ?? undefined,
      supportingText: person.email ?? undefined,
    }));
  }, [searchQuery, searchState, searchResults]);

  return (
    <AdminRoutedModal>
      {({ close }) => {
        const handleSubmit = () => {
          if (mode === "search" && selectedPerson) {
            router.post(
              adminAccountMembersPath(account_id),
              { person_id: selectedPerson.id },
              {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => close(),
              },
            );
          } else if (mode === "create" && newName.trim()) {
            router.post(
              adminAccountMembersPath(account_id),
              { person: { name: newName.trim(), date_of_birth: newDateOfBirth?.toString() ?? null } },
              {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => close(),
              },
            );
          }
        };

        return (
          <NewView
            mode={mode}
            canSubmit={canSubmit}
            onClose={close}
            onSubmit={handleSubmit}
            searchQuery={searchQuery}
            items={items}
            selectedPerson={selectedPerson}
            onSearchInputChange={handleSearchInputChange}
            onSelectionChange={handleSelectionChange}
            onClearSelectedPerson={handleClearSelectedPerson}
            onSwitchToCreate={() => setMode("create")}
            newName={newName}
            newDateOfBirth={newDateOfBirth}
            onNameChange={setNewName}
            onDateOfBirthChange={setNewDateOfBirth}
            onSwitchToSearch={() => setMode("search")}
          />
        );
      }}
    </AdminRoutedModal>
  );
};

AdminAccountMemberNewPage.layout = (page: ReactNode) => page;

export default AdminAccountMemberNewPage;
